import React from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { styled } from '@mui/material/styles';
import PropTypes from 'prop-types';
import { themeUtils } from '../../../theme';

const StyledCrossTabDataGrid = styled(DataGrid)(({ theme }) => ({
  border: `1px solid ${theme.palette.grey[200]}`,
  borderRadius: 0,
  backgroundColor: 'white',
  '& .MuiDataGrid-main': {
    borderRadius: 0,
  },
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: themeUtils.colors.blue['blue-50'],
    borderBottom: `2px solid ${themeUtils.colors.blue['blue-100']}`,
    '& .MuiDataGrid-columnHeader': {
      fontWeight: 600,
      fontSize: '0.875rem',
      color: theme.palette.primary.main,
    },
  },
  '& .MuiDataGrid-row': {
    '&:hover': {
      backgroundColor: theme.palette.grey[50],
    },
    '&.Mui-selected': {
      backgroundColor: `${theme.palette.primary.main}08`,
      '&:hover': {
        backgroundColor: `${theme.palette.primary.main}12`,
      },
    },
  },
  '& .MuiDataGrid-cell': {
    borderBottom: `1px solid ${theme.palette.grey[100]}`,
    display: 'flex',
    alignItems: 'center',
    '& .MuiDataGrid-cellContent': {
      width: '100%',
    },
  },
  '& .MuiDataGrid-footerContainer': {
    borderTop: `1px solid ${theme.palette.grey[200]}`,
    backgroundColor: theme.palette.grey[50],
  },
  '& .MuiDataGrid-toolbarContainer': {
    padding: theme.spacing(1, 2),
    borderBottom: `1px solid ${theme.palette.grey[200]}`,
    backgroundColor: theme.palette.grey[25],
  },
  // Enhanced scrollbars for CrossTab
  '& .MuiDataGrid-scrollbar--horizontal': {
    height: '20px !important',
  },
  '& .MuiDataGrid-scrollbar': {
    '&::-webkit-scrollbar': {
      height: 18,
      width: 18,
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: theme.palette.grey[100],
      borderRadius: 6,
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: theme.palette.primary.main,
      borderRadius: 6,
      border: `2px solid ${theme.palette.grey[100]}`,
      '&:hover': {
        backgroundColor: theme.palette.primary.dark,
      },
    },
  },
  // Pin left columns with enhanced styling
  '& .MuiDataGrid-columnHeader--pinnedLeft': {
    backgroundColor: theme.palette.grey[25],
    zIndex: 11,
    borderRight: `2px solid ${theme.palette.primary.main}`,
  },
  '& .MuiDataGrid-cell--pinnedLeft': {
    backgroundColor: 'white',
    zIndex: 9,
    borderRight: `2px solid ${theme.palette.primary.main}`,
  },
  // Enhanced month column headers for better readability
  '& .MuiDataGrid-columnHeaderTitle': {
    fontWeight: 600,
    fontSize: '0.75rem',
  },
}));

const CrossTabDataGrid = ({
  rows = [],
  columns = [],
  loading = false,
  checkboxSelection = false,
  disableRowSelectionOnClick = true,
  rowHeight = 60,
  headerHeight = 80,
  paginationMode = 'client',
  pageSize = 25,
  rowsPerPageOptions = [10, 25, 50, 100],
  disableColumnFilter = false,
  disableColumnMenu = false,
  scrollbarSize = 18,
  ...props
}) => {
  return (
    <StyledCrossTabDataGrid
      rows={rows}
      columns={columns}
      loading={loading}
      checkboxSelection={checkboxSelection}
      disableRowSelectionOnClick={disableRowSelectionOnClick}
      rowHeight={rowHeight}
      headerHeight={headerHeight}
      paginationMode={paginationMode}
      initialState={{
        pagination: {
          paginationModel: { pageSize },
        },
      }}
      pageSizeOptions={rowsPerPageOptions}
      disableColumnFilter={disableColumnFilter}
      disableColumnMenu={disableColumnMenu}
      scrollbarSize={scrollbarSize}
      // Disable virtualization completely for CrossTab to enable horizontal scrolling
      disableVirtualization={true}
      columnVirtualization={false}
      disableRowVirtualization={false}
      sx={{
        height: '100%',
        width: '100%',
        '& .MuiDataGrid-main': {
          overflow: 'auto',
        },
        '& .MuiDataGrid-virtualScroller': {
          overflow: 'auto !important',
        },
        '& .MuiDataGrid-columnHeaders': {
          minHeight: '80px !important',
        },
        '& .MuiDataGrid-columnHeader--withRightBorder': {
          borderRight: `1px solid ${props.theme?.palette?.grey?.[200] || '#e0e0e0'}`,
        },
        '& .MuiDataGrid-scrollbar--horizontal': {
          bottom: 0,
          position: 'sticky',
        },
      }}
      {...props}
    />
  );
};

CrossTabDataGrid.propTypes = {
  rows: PropTypes.array,
  columns: PropTypes.array,
  loading: PropTypes.bool,
  checkboxSelection: PropTypes.bool,
  disableRowSelectionOnClick: PropTypes.bool,
  rowHeight: PropTypes.number,
  headerHeight: PropTypes.number,
  paginationMode: PropTypes.oneOf(['client', 'server']),
  pageSize: PropTypes.number,
  rowsPerPageOptions: PropTypes.array,
  disableColumnFilter: PropTypes.bool,
  disableColumnMenu: PropTypes.bool,
  scrollbarSize: PropTypes.number,
};

export default CrossTabDataGrid; 